import {
  em,
  getBreakpointValue,
  getSpacing,
  InlineStyles,
  Breadcrumbs as MantineBreadcrumbs,
  Stepper as MantineStepper,
  Timeline as MantineTimeline,
  useRandomClassName,
} from "@mantine/core";
import * as React from "react";

import { tokens } from "../tokens";

import classes from "./navigation.module.css";
import { type ElementProps, rem, type SizeValue, type StyleProps, type ThemeColor } from "./style-props";

export interface BreadcrumbsProps extends ElementProps<"div">, StyleProps {
  /** Rendered between crumbs; `">"` at every call site today. */
  separator?: React.ReactNode;
  children: React.ReactNode;
}

/** The two Mantine 6 Breadcrumbs rules Mantine 7's stylesheet dropped; see `navigation.module.css`. */
const BREADCRUMBS_CLASS_NAMES = { root: classes.breadcrumbsRoot, separator: classes.breadcrumbsSeparator };

/**
 * Non-polymorphic (a plain `forwardRef` over a `<div>`), so no root cast is needed — the spread
 * below is structurally checked against Mantine's own `BreadcrumbsProps`, the same rule that keeps
 * `ThemeIcon` and `Skeleton` cast-free in the indicators module. Mantine 7 made the row wrap and
 * stopped sizing separators as `sm` text; the slot classes restore both (SC-2703). A consumer
 * `className` still lands on the root beside them.
 */
export const Breadcrumbs = React.forwardRef<HTMLDivElement, BreadcrumbsProps>(function Breadcrumbs({ children, ...props }, ref) {
  return (
    <MantineBreadcrumbs {...props} classNames={BREADCRUMBS_CLASS_NAMES} ref={ref}>
      {children}
    </MantineBreadcrumbs>
  );
});

export interface StepperProps extends ElementProps<"div">, StyleProps {
  children: React.ReactNode;
  active: number;
  onStepClick?: (stepIndex: number) => void;
  color?: ThemeColor;
  iconSize?: number;
  contentPadding?: SizeValue;
  orientation?: "vertical" | "horizontal";
  iconPosition?: "right" | "left";
  size?: SizeValue;
  radius?: SizeValue;
  breakpoint?: SizeValue;
  allowNextStepsSelect?: boolean;
}

export interface StepperStepProps extends ElementProps<"button">, StyleProps {
  children?: React.ReactNode;
  color?: ThemeColor;
  icon?: React.ReactNode;
  completedIcon?: React.ReactNode;
  progressIcon?: React.ReactNode;
  label?: React.ReactNode;
  description?: React.ReactNode;
  iconSize?: number;
  iconPosition?: "right" | "left";
  size?: SizeValue;
  radius?: SizeValue;
  loading?: boolean;
  allowStepSelect?: boolean;
}

/**
 * What the root adapter hands Mantine 7's Stepper: the owned props minus `breakpoint`, which Mantine 7
 * removed and the wrapper applies itself, plus the two Mantine 7 options the wrapper sets — `wrap`, and
 * owned class names on the `steps` and `separator` slots that the breakpoint rules target.
 */
type StepperRootProps = Omit<StepperProps, "breakpoint"> & {
  wrap?: boolean;
  classNames?: { steps?: string; separator?: string };
  readonly ref?: React.Ref<HTMLDivElement>;
};

/**
 * Mantine 7 declares `Stepper.size` as `MantineSize`, but routes the value through `getSize`,
 * `getFontSize` and `getSpacing`, whose runtime explicitly converts numbers to rem — as Mantine 6's
 * `getSize` did. The owned `SizeValue` therefore preserves a supported runtime input that the vendor
 * declaration omits. This private adapter cast is pinned by `navigation-casts.test.ts` against both
 * the declared forwarded props and Mantine 7's numeric `getSize` path.
 */
const StepperRoot = MantineStepper as React.ComponentType<StepperRootProps>;

/**
 * Mantine 6 rendered steps on one line and let `breakpoint` (a theme breakpoint key or a width) stack
 * them vertically below that width. Mantine 7 wraps steps by default and removed `breakpoint`, so the
 * wrapper turns wrapping off and re-creates Mantine 6's rules below the breakpoint: steps in a column
 * aligned to the icon side, each separator a vertical bar centred under the icon (SC-2703). A key
 * resolves through the breakpoint tokens, which are the theme's (Mantine's defaults). Mantine 6 also
 * defaulted `radius` to `xl`; Mantine 7 rounds fully.
 */
const StepperBase = React.forwardRef<HTMLDivElement, StepperProps>(function StepperBase(
  { children, breakpoint, radius = "xl", ...props },
  ref,
) {
  const stepsClassName = useRandomClassName();
  const separatorClassName = useRandomClassName();
  const query = breakpoint === undefined ? undefined : `(max-width: ${em(getBreakpointValue(breakpoint, tokens.breakpoint) - 1)})`;
  const iconEnd = props.iconPosition === "right";
  const separatorOffset = `calc(var(--stepper-icon-size) / 2 - ${rem(1)})`;

  return (
    <>
      <StepperRoot
        {...props}
        radius={radius}
        wrap={false}
        classNames={query === undefined ? undefined : { steps: stepsClassName, separator: separatorClassName }}
        ref={ref}
      >
        {children}
      </StepperRoot>
      {query !== undefined && (
        <>
          <InlineStyles
            selector={`.${stepsClassName}.${stepsClassName}`}
            media={[{ query, styles: { flexDirection: "column", alignItems: iconEnd ? "flex-end" : "flex-start" } }]}
          />
          <InlineStyles
            selector={`.${separatorClassName}.${separatorClassName}`}
            media={[
              {
                query,
                styles: {
                  width: rem(2),
                  minHeight: getSpacing("xl"),
                  marginLeft: iconEnd ? 0 : separatorOffset,
                  marginRight: iconEnd ? separatorOffset : 0,
                  marginTop: `calc(${getSpacing("xs")} / 2)`,
                  marginBottom: `calc(${getSpacing("xs")} - ${rem(2)})`,
                },
              },
            ]}
          />
        </>
      )}
    </>
  );
});

/**
 * Mantine 7's Step has no `size` or `radius`: the Stepper root sets both for every step. Mantine 6's
 * Stepper overwrote each step's own `size` and `radius` with its own when it cloned the step, so the
 * owned step props never took effect inside a Stepper and are dropped here (SC-2703). Every prop the
 * Stepper injects when it clones a step (`step`, `state`, `onClick`, …) passes through the spread.
 */
const StepperStep = React.forwardRef<HTMLButtonElement, StepperStepProps>(function StepperStep(
  { children, size: _size, radius: _radius, ...props },
  ref,
) {
  return (
    <MantineStepper.Step {...props} ref={ref}>
      {children}
    </MantineStepper.Step>
  );
});

export const Stepper = Object.assign(StepperBase, { Step: StepperStep });

export interface TimelineProps extends ElementProps<"div">, StyleProps {
  children: React.ReactNode;
  active?: number;
  color?: ThemeColor;
  radius?: SizeValue;
  bulletSize?: number | string;
  align?: "right" | "left";
  lineWidth?: number | string;
  reverseActive?: boolean;
}

export interface TimelineItemProps extends Omit<ElementProps<"div">, "title">, StyleProps {
  title?: React.ReactNode;
  bullet?: React.ReactNode;
  children?: React.ReactNode;
  color?: ThemeColor;
  lineVariant?: "solid" | "dashed" | "dotted";
}

/** Mantine 6 defaulted the Timeline `radius` to `xl`; Mantine 7 rounds fully. The wrapper keeps `xl` (SC-2703). */
const TimelineRoot = React.forwardRef<HTMLDivElement, TimelineProps>(function TimelineRoot({ children, radius = "xl", ...props }, ref) {
  return (
    <MantineTimeline {...props} radius={radius} ref={ref}>
      {children}
    </MantineTimeline>
  );
});

const TimelineItem = React.forwardRef<HTMLDivElement, TimelineItemProps>(function TimelineItem({ children, ...props }, ref) {
  return (
    <MantineTimeline.Item {...props} ref={ref}>
      {children}
    </MantineTimeline.Item>
  );
});

export const Timeline = Object.assign(TimelineRoot, { Item: TimelineItem });
